import React from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, MdFilterList, MdStore, MdList, getTabLabel, InlineEditableText
} from './ThemeLayoutHelpers';


export default function DawnLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#2563eb";
  const text = p.storefrontText || {};
  const copy = getHeroContent(p, {
    eyebrow: "Dawn Theme",
    title: "Clean shopping experience with modern rhythm.",
    subtitle: "Designed for clarity, product discovery, and effortless browsing across mobile and desktop.",
    primaryCta: "Shop Now",
    secondaryCta: "Learn More",
  });

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-sky-50 via-white to-slate-50" style={{ ...p.themeStyles, fontFamily: T.fontFamily || "Manrope, ui-sans-serif, system-ui" }}>
      <div className="w-full py-2 text-center text-xs font-semibold bg-white border-b border-sky-100 text-sky-700">
        {p.storeInfo?.businessCategory || p.storeInfo?.businessName}
      </div>

      <nav className="w-full sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <StoreLogo storeInfo={p.storeInfo} />
          <div className="hidden sm:block">
            <p className="text-sm font-bold text-slate-900">{p.storeInfo?.businessName}</p>
            <p className="text-xs text-slate-500">{text.storeInfo || "Store Info"}</p>
          </div>
          <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="flex-1 hidden md:block ml-2" />
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => p.setFilterPanelOpen(true)} className="hidden md:flex items-center gap-1 px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold" style={{ color: primary }}>
              <MdFilterList /> {text.filter || "Filter"}
            </button>
            <button onClick={p.handleGoToWishlist} className="relative p-2 rounded-xl border border-slate-200 hover:bg-slate-50" style={{ color: primary }}>
              <FaHeart className="text-lg" />
              {p.wishlistCount > 0 && <span className="absolute -top-1 -right-1 h-4 w-4 text-[9px] rounded-full bg-sky-600 text-white flex items-center justify-center">{p.wishlistCount}</span>}
            </button>
            <CartBtn p={p} />
            {p.userMenuElement}
          </div>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto w-full px-4 pt-6">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
          <div className="xl:col-span-7 rounded-3xl border border-white/60 shadow-[0_20px_60px_rgba(15,23,42,0.22)] overflow-hidden">
            <HeroImage p={p} className="h-[26rem]">
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/20 to-transparent" />
              <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-end">
                <p className="text-xs uppercase tracking-[0.2em] text-white/75"><InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText></p>
                <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mt-2 max-w-xl"><InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText></h1>
                <p className="text-white/80 mt-3 max-w-lg text-sm"><InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText></p>
                <div className="mt-6 flex gap-3">
                  <button onClick={() => p.setActiveTab("products")} className="px-5 py-2.5 rounded-xl text-sm font-bold text-white" style={{ backgroundColor: primary }}><InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText></button>
                  <button onClick={() => p.setActiveTab("details")} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white border border-white/45"><InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText></button>
                </div>
              </div>
            </HeroImage>
          </div>

          <div className="xl:col-span-5 rounded-3xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm">
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-sky-100 bg-sky-50 px-4 py-4">
                <p className="text-xs text-sky-700 font-semibold">{text.products || "Products"}</p>
                <p className="text-2xl font-extrabold text-sky-900 mt-1">{p.filteredProducts?.length || 0}</p>
              </div>
              <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-4">
                <p className="text-xs text-indigo-700 font-semibold">{text.services || "Services"}</p>
                <p className="text-2xl font-extrabold text-indigo-900 mt-1">{p.filteredServices?.length || 0}</p>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <div className="rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Fast Order Support</p>
                  <p className="text-xs text-slate-500">Direct call and WhatsApp help</p>
                </div>
                <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">Live</span>
              </div>
              <div className="rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Fresh Catalog Updates</p>
                  <p className="text-xs text-slate-500">New products added frequently</p>
                </div>
                <span className="text-xs font-bold text-sky-700 bg-sky-100 px-2 py-1 rounded-full">Weekly</span>
              </div>
            </div>

            <button
              onClick={() => p.setActiveTab("products")}
              className="mt-5 w-full px-4 py-3 rounded-xl text-sm font-bold text-white"
              style={{ backgroundColor: primary }}
            >
              {text.exploreAllProducts || "Explore All Products"}
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto w-full px-4 mt-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-2 flex gap-2 overflow-x-auto">
          {[
            ["products", getTabLabel(p, "products"), <MdList key="p" />],
            ["services", getTabLabel(p, "services"), <MdList key="s" />],
            ["details", getTabLabel(p, "details", "store-info"), <MdStore key="d" />],
          ].map(([key, label, icon]) => (
            <button
              key={key}
              onClick={() => p.setActiveTab(key)}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 whitespace-nowrap"
              style={p.activeTab === key ? { backgroundColor: primary, color: "#fff" } : { color: "#475569" }}
            >
              {icon} {label}
            </button>
          ))}
        </div>
      </section>

      <section className="flex-1 max-w-7xl mx-auto w-full px-4 py-6">
        <TabContent p={p} />
      </section>

      <StoreFooter p={p} />
    </div>
  );
}